import { Position } from '@deck.gl/core/typed';
import { Geometry } from '@turf/helpers';
import { createContext } from 'react';
import { ContextMenuItem } from '../MapMarker/types';

export interface MapMarkers {
  [key: string]: MapMarkerLayer;
}

export interface KineticModuleLayerData {
  /**
   * Id of the entity.
   */
  id: string;
  /**
   * Route for the entity drawer.
   */
  entityDrawerRoute?: string;
  /** Tooltip for icon. */
  getTooltip?: () => React.ReactNode;
  /** Type of marker. default is marker.  */
  type?: 'marker' | 'geojson';

  /**
   * Context menu items for the marker.
   */
  contextMenuItems?: ContextMenuItem[];
}

export interface ClusterSvg {
  svg: string;
  count: number;
}

export interface ClusterSvgs {
  [key: string]: ClusterSvg;
}

export interface ClusterKineticMarkerData extends KineticMarkerData {
  cluster: boolean;
  clusterGroups: ClusterSvgs;
}

export type OptionalClusterKineticMarkerData = ClusterKineticMarkerData | KineticMarkerData;

export interface KineticMarkerData extends KineticModuleLayerData {
  /** HTML for icon. */
  iconHtml: string;
  /**
   * Icon used for cluster.
   */
  clusterSvg: string;
  /**
   * String used to group markers into clusters.
   */
  clusterType: string;
  /** Position of icon. */
  coordinates: Position;
  /** Horizontal position of icon anchor. Default: half width. */
  anchorX?: number;
  /** Vertical position of icon anchor. Default: half height. */
  anchorY?: number;
  type?: 'marker';
  version?: number;
  elevation?: number;
}

export interface KineticGeoJsonData extends KineticModuleLayerData {
  /**
   * Geometry of the item.
   */
  geometry: Geometry;
  /**
   * Fill color for the layer.
   */
  fillColor?: [number, number, number, number];
  /**
   * Line color for the layer.
   */
  lineColor?: [number, number, number, number];
  /**
   * Line width for the layer.
   */
  lineWidth?: number;
  /**
   * Elevation for the layer.
   */
  elevation?: number;
  extruded?: boolean;
  type: 'geojson';
  version?: number;
}

export interface MapMarkerLayer<TData extends KineticMarkerData = any> {
  data: Map<string, TData>;
}

export interface MapMarkerContextData {
  lastUpdate: Date;
  data: Map<string, any>;
  intersectsWithData: (geometry: Geometry) => string[];
  mapMarkers: MapMarkers;
}

export interface MapMarkerContextFunctionsInterface {
  updateData: (layerId: string, data: Map<string, any>) => void;
  addLayer: (layerId: string, layer: MapMarkerLayer) => void;
  addToCache: (html: string) => Promise<void>;
  removeLayer: (layerId: string) => void;
}

export const MapMarkerContext = createContext<MapMarkerContextData>({
  lastUpdate: new Date(),
  data: new Map(),
  intersectsWithData: () => [],
  mapMarkers: {},
});

export const MapMarkerContextFunctions = createContext<MapMarkerContextFunctionsInterface>({
  updateData: () => null,
  addLayer: () => null,
  addToCache: () => Promise.reject(),
  removeLayer: () => null,
});

export interface DeckGLIconProps {
  url: string;
  width: number;
  height: number;
  anchorY: number;
  anchorX: number;
  mask: boolean;
}
